import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, apiClient } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { MoradasSection } from '../components/MoradasSection';
import {
  FileText, Package, HandHeart, MessageSquare, ClipboardCheck,
  CheckCircle2, XCircle, Clock, AlertTriangle, Calendar, Star,
  AlertCircle, Award, Shield, TrendingUp, Download, Loader2, User, ChevronRight,
  History, Plus, Trash2, CheckCircle, Eye, ShieldAlert, Mail, Bell, ArrowLeft,
  Edit, Heart, MapPin, Phone, Users, Building2
} from 'lucide-react';
import { toast } from 'sonner';

// Estado da Instituição (4º Steering ENTRAJUDA) - reflete a relação institucional em tempo real
const ESTADO_INSTITUICAO_CONFIG = {
  'Em análise': { color: 'bg-orange-500', label: 'Em análise' },
  'Ativa': { color: 'bg-green-500', label: 'Ativa' },
  'Suspensa': { color: 'bg-red-600', label: 'Suspensa' },
  'Inativa': { color: 'bg-red-600', label: 'Inativa' }
};

// Estado da Candidatura - reflete o resultado do processo de admissão (não se altera depois de concluído)
const ESTADO_CANDIDATURA_CONFIG = {
  'Submetida': { color: 'bg-blue-500', label: 'Submetida' },
  'Em análise': { color: 'bg-orange-500', label: 'Em análise' },
  'Aprovada': { color: 'bg-green-500', label: 'Aprovada' },
  'Rejeitada': { color: 'bg-red-600', label: 'Rejeitada' },
  'Cancelada': { color: 'bg-slate-500', label: 'Cancelada' },
  'Sem registo': { color: 'bg-slate-400', label: 'Sem registo' }
};

export default function InstituicaoDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  
  const [instituicao, setInstituicao] = useState(null);
  const [dadosRelacionados, setDadosRelacionados] = useState(null);
  const [qualidadeInfo, setQualidadeInfo] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [acoesPendentes, setAcoesPendentes] = useState(null);
  const [resultadoAvaliacao, setResultadoAvaliacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingTipoMembro, setUpdatingTipoMembro] = useState(false);
  const [estadoModalOpen, setEstadoModalOpen] = useState(false);
  const [novoEstado, setNovoEstado] = useState('');
  const [motivoEstado, setMotivoEstado] = useState('');
  const [updatingEstado, setUpdatingEstado] = useState(false);
  const [enviandoConvite, setEnviandoConvite] = useState(false);
  const [notificacoesPortal, setNotificacoesPortal] = useState([]);
  const [loadingNotificacoes, setLoadingNotificacoes] = useState(false);
  
  // Estados para as novas tabs
  const [documentosPendentes, setDocumentosPendentes] = useState([]);
  const [documentosHistorico, setDocumentosHistorico] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [contactosRegistados, setContactosRegistados] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState({});
  
  // Estados para formulários
  const [novoDocumento, setNovoDocumento] = useState({ tipo: '', descricao: '' });
  const [novoContacto, setNovoContacto] = useState({ tipo: '', assunto: '', descricao: '' });
  const [historicoDetalhe, setHistoricoDetalhe] = useState(null);

  // Helper para calcular cor de atualização de dados
  const getAtualizacaoDadosConfig = (dataUltimaAtualizacao) => {
    if (!dataUltimaAtualizacao) {
      return { color: 'bg-red-500', textColor: 'text-red-600', label: 'Nunca', bgClass: 'bg-red-100 dark:bg-red-900/30' };
    }
    
    const now = new Date();
    const anoAtual = now.getFullYear();
    const dataAtualizacao = new Date(dataUltimaAtualizacao);
    const anoAtualizacao = dataAtualizacao.getFullYear();
    const diff = anoAtual - anoAtualizacao;
    
    if (diff === 0) {
      return { 
        color: 'bg-green-500', 
        textColor: 'text-green-600 dark:text-green-400', 
        label: anoAtualizacao.toString(), 
        bgClass: 'bg-green-100 dark:bg-green-900/30',
        tooltip: 'Dados atualizados este ano'
      };
    } else if (diff === 1) {
      return { 
        color: 'bg-yellow-500', 
        textColor: 'text-yellow-600 dark:text-yellow-400', 
        label: anoAtualizacao.toString(), 
        bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
        tooltip: 'Dados com 1 ano de atraso'
      };
    } else {
      return { 
        color: 'bg-red-500', 
        textColor: 'text-red-600 dark:text-red-400', 
        label: anoAtualizacao.toString(), 
        bgClass: 'bg-red-100 dark:bg-red-900/30',
        tooltip: `Dados com ${diff} anos de atraso`
      };
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [instData, relData] = await Promise.all([
        api.getInstituicao(id),
        api.getInstituicaoDadosRelacionados(id)
      ]);
      setInstituicao(instData);
      setDadosRelacionados(relData);
      
      // Carregar dados adicionais
      try {
        const [qualidade, acoes, avaliacao] = await Promise.all([
          apiClient.get(`/instituicoes/${id}/qualidade-informacao`),
          apiClient.get(`/instituicoes/${id}/acoes-pendentes`),
          apiClient.get(`/instituicoes/${id}/resultado-avaliacao`)
        ]);
        setQualidadeInfo(qualidade.data);
        setAcoesPendentes(acoes.data);
        setResultadoAvaliacao(avaliacao.data);
      } catch (e) {
        console.error('Erro ao carregar dados adicionais:', e);
      }
      
      // Carregar histórico de notificações do portal
      try {
        setLoadingNotificacoes(true);
        const notifResponse = await apiClient.get(`/instituicoes/${id}/notificacoes-portal`);
        setNotificacoesPortal(notifResponse.data || []);
      } catch (e) {
        // Silencioso — pode não ter notificações
      } finally {
        setLoadingNotificacoes(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados da instituição');
      navigate('/instituicoes');
    } finally {
      setLoading(false);
    }
  };

  const handleTipoMembroChange = async (newTipo) => {
    setUpdatingTipoMembro(true);
    try {
      await api.updateInstituicaoTipoMembro(id, newTipo);
      setInstituicao(prev => ({ ...prev, tipo_membro: newTipo }));
      toast.success(`Instituição marcada como ${newTipo === 'efetiva' ? 'Efetiva' : 'Candidata'}`);
    } catch (error) {
      console.error('Error updating tipo_membro:', error);
      toast.error('Erro ao atualizar tipo de membro');
    } finally {
      setUpdatingTipoMembro(false);
    }
  };

  const openEstadoModal = () => {
    setNovoEstado(instituicao.estado || 'Ativa');
    setMotivoEstado('');
    setEstadoModalOpen(true);
  };

  const handleUpdateEstado = async () => {
    if (['Suspensa', 'Inativa'].includes(novoEstado) && !motivoEstado.trim()) {
      toast.error('É obrigatório indicar o motivo para este estado');
      return;
    }
    setUpdatingEstado(true);
    try {
      const updated = await api.updateInstituicaoEstado(id, novoEstado, motivoEstado.trim() || undefined);
      setInstituicao(updated);
      toast.success(`Estado da Instituição atualizado para "${novoEstado}"`);
      setEstadoModalOpen(false);
    } catch (error) {
      console.error('Error updating estado:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar estado');
    } finally {
      setUpdatingEstado(false);
    }
  };

  const handleEnviarConvite = async () => {
    setEnviandoConvite(true);
    try {
      await api.enviarConviteCandidatura(id);
      toast.success('Convite enviado com sucesso (simulado)');
    } catch (error) {
      toast.error('Erro ao enviar convite');
    } finally {
      setEnviandoConvite(false);
    }
  };

  // ==================== FUNÇÕES PARA DOCUMENTOS ====================
  const loadDocumentos = async () => {
    setLoadingTabs(prev => ({ ...prev, documentos: true }));
    try {
      const response = await apiClient.get(`/instituicoes/${id}/documentos`);
      setDocumentosPendentes(response.data.pendentes || []);
      setDocumentosHistorico(response.data.historico || []);
    } catch (error) {
      console.error('Error loading documentos:', error);
    } finally {
      setLoadingTabs(prev => ({ ...prev, documentos: false }));
    }
  };

  const addDocumentoPendente = async () => {
    if (!novoDocumento.tipo) {
      toast.error('Selecione o tipo de documento');
      return;
    }
    try {
      await apiClient.post(`/instituicoes/${id}/documentos`, novoDocumento);
      toast.success('Documento adicionado');
      setNovoDocumento({ tipo: '', descricao: '' });
      loadDocumentos();
    } catch (error) {
      toast.error('Erro ao adicionar documento');
    }
  };

  const marcarDocumentoEntregue = async (docId) => {
    try {
      await apiClient.put(`/instituicoes/${id}/documentos/${docId}/entregue`);
      toast.success('Documento marcado como entregue');
      loadDocumentos();
    } catch (error) {
      toast.error('Erro ao marcar documento');
    }
  };

  // ==================== FUNÇÕES PARA HISTÓRICO ====================
  const loadHistorico = async () => {
    setLoadingTabs(prev => ({ ...prev, historico: true }));
    try {
      const response = await apiClient.get(`/instituicoes/${id}/historico`);
      setHistorico(response.data || []);
    } catch (error) {
      console.error('Error loading historico:', error);
    } finally {
      setLoadingTabs(prev => ({ ...prev, historico: false }));
    }
  };

  // ==================== FUNÇÕES PARA CONTACTOS ====================
  const loadContactos = async () => {
    setLoadingTabs(prev => ({ ...prev, contactos: true }));
    try {
      const response = await apiClient.get(`/instituicoes/${id}/contactos`);
      setContactosRegistados(response.data || []);
    } catch (error) {
      console.error('Error loading contactos:', error);
    } finally {
      setLoadingTabs(prev => ({ ...prev, contactos: false }));
    }
  };

  const addContactoRegistado = async () => {
    if (!novoContacto.tipo || !novoContacto.assunto) {
      toast.error('Preencha o tipo e assunto do contacto');
      return;
    }
    try {
      await apiClient.post(`/instituicoes/${id}/contactos`, novoContacto);
      toast.success('Contacto registado');
      setNovoContacto({ tipo: '', assunto: '', descricao: '' });
      loadContactos();
    } catch (error) {
      toast.error('Erro ao registar contacto');
    }
  };

  const deleteContactoRegistado = async (contactoId) => {
    try {
      await apiClient.delete(`/instituicoes/${id}/contactos/${contactoId}`);
      toast.success('Contacto removido');
      loadContactos();
    } catch (error) {
      toast.error('Erro ao remover contacto');
    }
  };

  // ==================== FUNÇÕES PARA MENSAGENS ====================
  const loadMensagens = async () => {
    setLoadingTabs(prev => ({ ...prev, mensagens: true }));
    try {
      const response = await apiClient.get(`/instituicoes/${id}/mensagens`);
      setMensagens(response.data || []);
    } catch (error) {
      console.error('Error loading mensagens:', error);
    } finally {
      setLoadingTabs(prev => ({ ...prev, mensagens: false }));
    }
  };

  // Carregar dados das tabs quando necessário
  const handleTabChange = (tab) => {
    if (tab === 'documentos-pendentes' && documentosPendentes.length === 0) loadDocumentos();
    if (tab === 'historico' && historico.length === 0) loadHistorico();
    if (tab === 'contactos-internos' && contactosRegistados.length === 0) loadContactos();
    if (tab === 'mensagens' && mensagens.length === 0) loadMensagens();
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pendente': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      'Aprovado': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Rejeitado': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      'Em Trânsito': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Concluído': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Aberta': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Em Curso': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'Aberto': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Fechado': 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/relatorios/instituicao/${id}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        throw new Error('Erro ao gerar PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `instituicao_${instituicao.codigo_ea || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="instituicao-detalhe-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!instituicao) return null;

  const stats = dadosRelacionados?.stats || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="instituicao-detalhe-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/instituicoes')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{instituicao.nome}</h1>
              <Badge className={instituicao.tipo_membro === 'efetiva' ? 'bg-green-500' : 'bg-amber-500'}>
                {instituicao.tipo_membro === 'efetiva' ? 'Efetiva' : 'Candidata'}
              </Badge>
              {instituicao.certificada_ea && (
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white flex items-center gap-1">
                  <Award className="w-3 h-3" />
                  Certificada EA
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">Código: {instituicao.codigo_ea}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Estado da Instituição:</span>
              <Badge
                data-testid="estado-instituicao-badge"
                className={ESTADO_INSTITUICAO_CONFIG[instituicao.estado]?.color || 'bg-slate-400'}
                title={instituicao.motivo_estado || ''}
              >
                {ESTADO_INSTITUICAO_CONFIG[instituicao.estado]?.label || instituicao.estado || 'Ativa'}
              </Badge>
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">Estado da Candidatura:</span>
              <Badge
                data-testid="estado-candidatura-badge"
                className={ESTADO_CANDIDATURA_CONFIG[instituicao.estado_candidatura]?.color || 'bg-slate-400'}
              >
                {ESTADO_CANDIDATURA_CONFIG[instituicao.estado_candidatura]?.label || instituicao.estado_candidatura || 'Sem registo'}
              </Badge>
            </div>
            {instituicao.motivo_estado && ['Suspensa', 'Inativa'].includes(instituicao.estado) && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> Motivo: {instituicao.motivo_estado}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportPdf}
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
            className="text-[#0066CC] border-[#0066CC] hover:bg-[#0066CC]/10"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar PDF
          </Button>
          {instituicao.estado === 'Ativa' && instituicao.estado_candidatura === 'Sem registo' && (
            <Button
              variant="outline"
              onClick={handleEnviarConvite}
              disabled={enviandoConvite}
              data-testid="enviar-convite-btn"
              className="text-amber-600 border-amber-400 hover:bg-amber-50"
            >
              {enviandoConvite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Convidar para Candidatura
            </Button>
          )}
          <Button variant="outline" onClick={openEstadoModal} data-testid="alterar-estado-btn">
            <ShieldAlert className="w-4 h-4 mr-2" />
            Alterar Estado
          </Button>
          <Select 
            value={instituicao.tipo_membro || 'candidata'} 
            onValueChange={handleTipoMembroChange}
            disabled={updatingTipoMembro}
          >
            <SelectTrigger className="w-[180px]" data-testid="tipo-membro-select">
              <SelectValue placeholder="Tipo de Membro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="candidata">Candidata</SelectItem>
              <SelectItem value="efetiva">Efetiva</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate(`/instituicoes?edit=${id}`)} data-testid="edit-btn">
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      {/* Modal: Alterar Estado da Instituição */}
      <Dialog open={estadoModalOpen} onOpenChange={setEstadoModalOpen}>
        <DialogContent className="max-w-md" data-testid="alterar-estado-modal">
          <DialogHeader>
            <DialogTitle>Alterar Estado da Instituição</DialogTitle>
            <DialogDescription>
              O Estado da Instituição determina, em tempo real, se pode receber apoio da ENTRAJUDA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Novo Estado</Label>
              <Select value={novoEstado} onValueChange={setNovoEstado}>
                <SelectTrigger data-testid="novo-estado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativa">Ativa</SelectItem>
                  <SelectItem value="Suspensa">Suspensa</SelectItem>
                  <SelectItem value="Inativa">Inativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {['Suspensa', 'Inativa'].includes(novoEstado) && (
              <div>
                <Label className="text-xs text-slate-500">Motivo (obrigatório)</Label>
                <Textarea
                  value={motivoEstado}
                  onChange={(e) => setMotivoEstado(e.target.value)}
                  placeholder="Indique o motivo desta alteração de estado..."
                  rows={3}
                  data-testid="motivo-estado-textarea"
                />
              </div>
            )}
            {novoEstado === 'Em análise' && (
              <p className="text-xs text-slate-500">
                Deve constar um alerta a pedir confirmação antes de avançar com um pedido de apoio.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEstadoModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateEstado} disabled={updatingEstado} data-testid="confirmar-estado-btn">
              {updatingEstado ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#0066CC]" />
              Informações da Instituição
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dados Básicos */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Dados de Identificação
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Tipo de Entidade</p>
                  <p className="font-medium dark:text-white">{instituicao.tipo_entidade || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">NIF</p>
                  <p className="font-medium font-mono dark:text-white">{instituicao.nif || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Nº Registo IPSS</p>
                  <p className="font-medium dark:text-white">{instituicao.num_registo_ipss || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Data de Constituição</p>
                  <p className="font-medium dark:text-white">
                    {instituicao.data_constituicao ? new Date(instituicao.data_constituicao).toLocaleDateString('pt-PT') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Situação Financeira</p>
                  <Badge variant="outline">{instituicao.situacao_financeira || '-'}</Badge>
                </div>
              </div>
            </div>

            {/* Localização */}
            <div className="border-t pt-4 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Localização
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="col-span-2 md:col-span-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Morada</p>
                  <p className="font-medium dark:text-white">{instituicao.morada || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Código Postal</p>
                  <p className="font-medium dark:text-white">{instituicao.codigo_postal || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Distrito</p>
                  <p className="font-medium dark:text-white">{instituicao.distrito_nome || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Concelho</p>
                  <p className="font-medium dark:text-white">{instituicao.concelho_nome || '-'}</p>
                </div>
                {(instituicao.latitude && instituicao.longitude) && (
                  <div className="col-span-2 md:col-span-3">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Coordenadas GPS</p>
                    <p className="font-medium font-mono text-sm dark:text-white">
                      {instituicao.latitude?.toFixed(6)}, {instituicao.longitude?.toFixed(6)}
                      <a 
                        href={`https://www.google.com/maps?q=${instituicao.latitude},${instituicao.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-[#0066CC] hover:underline text-xs"
                      >
                        Ver no mapa ↗
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Contactos */}
            <div className="border-t pt-4 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Contactos
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Telefone</p>
                  <p className="font-medium dark:text-white">{instituicao.telefone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Telemóvel</p>
                  <p className="font-medium dark:text-white">{instituicao.telemovel || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                  <p className="font-medium dark:text-white truncate">{instituicao.email_principal || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Website</p>
                  <p className="font-medium dark:text-white">
                    {instituicao.website ? (
                      <a href={instituicao.website} target="_blank" rel="noopener noreferrer" className="text-[#0066CC] hover:underline">
                        {instituicao.website}
                      </a>
                    ) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Pessoa de Contacto</p>
                  <p className="font-medium dark:text-white">{instituicao.pessoa_contacto || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Representante Legal</p>
                  <p className="font-medium dark:text-white">{instituicao.representante_legal || '-'}</p>
                </div>
              </div>
            </div>

            {/* Dados Segurança Social */}
            <div className="border-t pt-4 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Segurança Social
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">NISS</p>
                  <p className="font-medium font-mono dark:text-white">{instituicao.niss || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Acordo de Cooperação</p>
                  <Badge variant={instituicao.acordo_cooperacao ? 'default' : 'outline'} className={instituicao.acordo_cooperacao ? 'bg-green-500' : ''}>
                    {instituicao.acordo_cooperacao ? 'Sim' : 'Não'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Nº Acordo</p>
                  <p className="font-medium dark:text-white">{instituicao.num_acordo_cooperacao || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Data do Acordo</p>
                  <p className="font-medium dark:text-white">
                    {instituicao.data_acordo_cooperacao ? new Date(instituicao.data_acordo_cooperacao).toLocaleDateString('pt-PT') : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Dados Fiscais */}
            <div className="border-t pt-4 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Dados Fiscais
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">CAE</p>
                  <p className="font-medium font-mono dark:text-white">{instituicao.cae || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Certidão Permanente</p>
                  <p className="font-medium font-mono dark:text-white">{instituicao.certidao_permanente || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">IBAN</p>
                  <p className="font-medium font-mono dark:text-white text-sm">{instituicao.iban || '-'}</p>
                </div>
              </div>
            </div>

            {/* Banco Alimentar */}
            <div className="border-t pt-4 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-500" />
                Banco Alimentar
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Recebe Apoio do Banco Alimentar</p>
                  <Badge variant={instituicao.apoio_banco_alimentar ? 'default' : 'outline'} className={instituicao.apoio_banco_alimentar ? 'bg-green-500' : ''}>
                    {instituicao.apoio_banco_alimentar ? 'Sim' : 'Não'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Código BA</p>
                  <p className={`font-bold font-mono ${instituicao.codigo_banco_alimentar ? 'text-[#0066CC] dark:text-blue-400 text-lg' : 'text-slate-400 dark:text-slate-600'}`}>
                    {instituicao.codigo_banco_alimentar || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Presidente / Representante Legal (Campos do Formulário) */}
            {(instituicao.presidente_nome || instituicao.presidente_email) && (
              <div className="border-t pt-4 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Presidente / Representante Legal
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Nome</p>
                    <p className="font-medium dark:text-white">{instituicao.presidente_nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Cargo</p>
                    <p className="font-medium dark:text-white">{instituicao.presidente_cargo || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Telefone</p>
                    <p className="font-medium dark:text-white">{instituicao.presidente_telefone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                    <p className="font-medium dark:text-white truncate">{instituicao.presidente_email || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Pessoa de Contacto Institucional (Campos do Formulário) */}
            {(instituicao.contacto_nome || instituicao.contacto_email) && (
              <div className="border-t pt-4 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Pessoa de Contacto Institucional
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Nome</p>
                    <p className="font-medium dark:text-white">{instituicao.contacto_nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Cargo</p>
                    <p className="font-medium dark:text-white">{instituicao.contacto_cargo || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Telefone</p>
                    <p className="font-medium dark:text-white">{instituicao.contacto_telefone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                    <p className="font-medium dark:text-white truncate">{instituicao.contacto_email || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Sobre a Instituição (Campos do Formulário) */}
            {(instituicao.missao || instituicao.populacao_apoiada || instituicao.area_geografica) && (
              <div className="border-t pt-4 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Sobre a Instituição
                </h4>
                <div className="space-y-4">
                  {instituicao.missao && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Missão</p>
                      <p className="font-medium dark:text-white text-sm">{instituicao.missao}</p>
                    </div>
                  )}
                  {instituicao.populacao_apoiada && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">População Apoiada</p>
                      <p className="font-medium dark:text-white text-sm">{instituicao.populacao_apoiada}</p>
                    </div>
                  )}
                  {instituicao.area_geografica && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Área Geográfica de Atuação</p>
                      <p className="font-medium dark:text-white text-sm">{instituicao.area_geografica}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Áreas de Interesse */}
            {instituicao.areas_interesse?.length > 0 && (
              <div className="border-t pt-4 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Áreas de Interesse</h4>
                <div className="flex flex-wrap gap-2">
                  {instituicao.areas_interesse.map((area) => (
                    <Badge key={area} variant="outline" className="bg-slate-100 dark:bg-slate-700">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-[#0066CC]/10 rounded-lg">
                <p className="text-2xl font-bold text-[#0066CC]">{instituicao.total_utentes || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Utentes</p>
              </div>
              <div className="text-center p-3 bg-[#00AA66]/10 rounded-lg">
                <p className="text-2xl font-bold text-[#00AA66]">{instituicao.num_tecnicos || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Técnicos</p>
              </div>
              <div className="text-center p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{instituicao.num_voluntarios || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Voluntários</p>
              </div>
              <div className="text-center p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{instituicao.valencias?.length || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Respostas Sociais</p>
              </div>
            </div>

            <div className="pt-4 border-t dark:border-slate-700 space-y-2">
              {/* Atualização de Dados - Indicador de Cor */}
              {(() => {
                const atualizacaoConfig = getAtualizacaoDadosConfig(instituicao.updated_at || instituicao.ultima_atualizacao);
                return (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Atualização Dados</span>
                    <div className="flex items-center gap-2" title={atualizacaoConfig.tooltip}>
                      <span className={`w-3 h-3 rounded-full ${atualizacaoConfig.color}`}></span>
                      <span className={`font-bold ${atualizacaoConfig.textColor}`}>
                        {atualizacaoConfig.label}
                      </span>
                    </div>
                  </div>
                );
              })()}
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Pedidos</span>
                <span className="font-medium dark:text-white">{stats.total_pedidos || 0} ({stats.pedidos_pendentes || 0} pendentes)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Missões</span>
                <span className="font-medium dark:text-white">{stats.total_missoes || 0} ({stats.missoes_ativas || 0} ativas)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Tickets</span>
                <span className="font-medium dark:text-white">{stats.total_tickets || 0} ({stats.tickets_abertos || 0} abertos)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Documentos</span>
                <span className="font-medium dark:text-white">{stats.total_documentos || 0} ({stats.documentos_validos || 0} válidos)</span>
              </div>
            </div>

            {/* Certificação EA */}
            {instituicao.certificada_ea ? (
              <div className="pt-4 border-t dark:border-slate-700">
                <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-800 dark:text-amber-300">Instituição Certificada EA</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400">ID: {instituicao.certificado_ea_id}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                    <div>
                      <p className="text-amber-600 dark:text-amber-400 text-xs">Score</p>
                      <p className="font-bold text-amber-800 dark:text-amber-300">{instituicao.ultimo_score_avaliacao || 0}</p>
                    </div>
                    <div>
                      <p className="text-amber-600 dark:text-amber-400 text-xs">Classificação</p>
                      <p className="font-bold text-amber-800 dark:text-amber-300">{instituicao.ultima_classificacao || '-'}</p>
                    </div>
                  </div>
                  {instituicao.validade_certificacao && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      Válido até: {new Date(instituicao.validade_certificacao).toLocaleDateString('pt-PT')}
                    </p>
                  )}
                </div>
              </div>
            ) : instituicao.ultimo_score_avaliacao ? (
              <div className="pt-4 border-t dark:border-slate-700">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Última Avaliação</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-[#0066CC]">{instituicao.ultimo_score_avaliacao}</span>
                      <Badge variant="outline">{instituicao.ultima_classificacao}</Badge>
                    </div>
                    {instituicao.ultimo_score_avaliacao < 60 && (
                      <span className="text-xs text-slate-500">Score mínimo para certificação: 60</span>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Qualidade da Informação e Ações Pendentes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Qualidade da Informação */}
        {qualidadeInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Qualidade da Informação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {qualidadeInfo.badge === 'excelente' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                  {qualidadeInfo.badge === 'bom' && <CheckCircle2 className="w-5 h-5 text-blue-500" />}
                  {qualidadeInfo.badge === 'atencao' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                  {qualidadeInfo.badge === 'desatualizado' && <XCircle className="w-5 h-5 text-red-500" />}
                  <span className="font-medium text-lg dark:text-white">{qualidadeInfo.badge_label}</span>
                </div>
                <Badge className={`
                  ${qualidadeInfo.badge === 'excelente' ? 'bg-green-100 text-green-800' : ''}
                  ${qualidadeInfo.badge === 'bom' ? 'bg-blue-100 text-blue-800' : ''}
                  ${qualidadeInfo.badge === 'atencao' ? 'bg-yellow-100 text-yellow-800' : ''}
                  ${qualidadeInfo.badge === 'desatualizado' ? 'bg-red-100 text-red-800' : ''}
                `}>
                  Score: {qualidadeInfo.score}/100
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Campos preenchidos</span>
                  <span className="font-medium dark:text-white">{qualidadeInfo.campos_preenchidos}/{qualidadeInfo.total_campos}</span>
                </div>
                <Progress value={qualidadeInfo.percentagem_preenchimento} className="h-2" />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Campanha {qualidadeInfo.campanha_ano}</span>
                {qualidadeInfo.campanha_respondida ? (
                  <Badge className="bg-green-100 text-green-800">Respondida</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800">Não respondida</Badge>
                )}
              </div>
              
              {qualidadeInfo.campos_em_falta?.length > 0 && (
                <div className="pt-2 border-t dark:border-slate-700">
                  <p className="text-sm font-medium text-red-500 mb-2">Campos em falta:</p>
                  <div className="flex flex-wrap gap-1">
                    {qualidadeInfo.campos_em_falta.map(campo => (
                      <Badge key={campo.campo} variant="outline" className="text-xs text-red-500 border-red-200">
                        {campo.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ações Pendentes */}
        {acoesPendentes && acoesPendentes.total_acoes > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Ações Pendentes ({acoesPendentes.total_acoes})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {acoesPendentes.acoes.map(acao => (
                  <div 
                    key={acao.id} 
                    className={`p-3 rounded-lg border ${
                      acao.prioridade === 'alta' ? 'border-red-200 bg-red-50 dark:bg-red-900/20' :
                      acao.prioridade === 'media' ? 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20' :
                      'border-slate-200 bg-slate-50 dark:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm dark:text-white">{acao.titulo}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{acao.descricao}</p>
                      </div>
                      <Badge variant="outline" className={`text-xs ${
                        acao.prioridade === 'alta' ? 'text-red-600' :
                        acao.prioridade === 'media' ? 'text-yellow-600' :
                        'text-slate-600'
                      }`}>
                        {acao.prioridade}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Histórico de Notificações Enviadas ao Portal */}
      {(notificacoesPortal.length > 0 || loadingNotificacoes) && (
        <Card data-testid="card-notificacoes-portal">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="w-5 h-5 text-[#0066CC]" />
              Notificações Enviadas ao Portal
              {notificacoesPortal.filter(n => !n.lida).length > 0 && (
                <Badge className="bg-orange-500 text-xs ml-auto">
                  {notificacoesPortal.filter(n => !n.lida).length} não lida(s)
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingNotificacoes ? (
              <div className="flex items-center gap-2 py-4 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">A carregar...</span>
              </div>
            ) : (
              <div className="space-y-2">
                {notificacoesPortal.slice(0, 8).map((notif) => (
                  <div
                    key={notif.id}
                    data-testid={`notif-portal-item-${notif.id}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      !notif.lida
                        ? 'border-[#0066CC]/30 bg-blue-50/50 dark:bg-blue-900/10'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
                    }`}
                  >
                    <Bell className={`w-4 h-4 mt-0.5 shrink-0 ${!notif.lida ? 'text-[#0066CC]' : 'text-slate-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{notif.titulo}</span>
                        {!notif.lida && (
                          <Badge className="bg-[#0066CC] text-[10px] px-1.5 py-0">Nova</Badge>
                        )}
                        {notif.tipo && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">
                            {notif.tipo}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{notif.mensagem}</p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                        {notif.created_at ? new Date(notif.created_at).toLocaleString('pt-PT') : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {notificacoesPortal.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhuma notificação enviada a esta instituição.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Atividade Social - Dados do Formulário de Registo */}
      {(instituicao.total_utentes_apoiados || instituicao.utentes_por_resposta_social || instituicao.distribuicao_bens || instituicao.outras_respostas_sociais) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-cyan-500" />
              Atividade Social
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Total de Utentes */}
              {instituicao.total_utentes_apoiados && (
                <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total de Utentes Apoiados</p>
                  <p className="text-3xl font-bold text-cyan-700 dark:text-cyan-300">{instituicao.total_utentes_apoiados}</p>
                </div>
              )}

              {/* Utentes por Resposta Social */}
              {instituicao.utentes_por_resposta_social && Object.keys(instituicao.utentes_por_resposta_social).length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-3">Utentes por Resposta Social</p>
                  <div className="space-y-2">
                    {Object.entries(instituicao.utentes_por_resposta_social).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm bg-white dark:bg-slate-700 p-2 rounded">
                        <span className="text-slate-600 dark:text-slate-300">{key}</span>
                        <span className="font-bold text-blue-600 dark:text-blue-400">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Distribuição de Bens */}
              {instituicao.distribuicao_bens && Object.keys(instituicao.distribuicao_bens).length > 0 && (
                <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                  <p className="text-sm font-medium text-pink-700 dark:text-pink-300 mb-3">Distribuição de Bens</p>
                  <div className="space-y-2">
                    {Object.entries(instituicao.distribuicao_bens).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm bg-white dark:bg-slate-700 p-2 rounded">
                        <span className="text-slate-600 dark:text-slate-300">{key}</span>
                        <span className="font-bold text-pink-600 dark:text-pink-400">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Outras Respostas Sociais */}
              {instituicao.outras_respostas_sociais && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg lg:col-span-2">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">Outras Respostas Sociais</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{instituicao.outras_respostas_sociais}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado da Avaliação */}
      {resultadoAvaliacao?.tem_avaliacao && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              Resultado da Última Avaliação
            </CardTitle>
            <CardDescription>
              {resultadoAvaliacao.tipo_avaliacao} - {resultadoAvaliacao.data_avaliacao ? new Date(resultadoAvaliacao.data_avaliacao).toLocaleDateString('pt-PT') : 'N/A'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1">
                <div className={`text-center p-6 rounded-lg ${
                  resultadoAvaliacao.classificacao_cor === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                  resultadoAvaliacao.classificacao_cor === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  resultadoAvaliacao.classificacao_cor === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                  'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <p className={`text-4xl font-bold ${
                    resultadoAvaliacao.classificacao_cor === 'green' ? 'text-green-600' :
                    resultadoAvaliacao.classificacao_cor === 'blue' ? 'text-blue-600' :
                    resultadoAvaliacao.classificacao_cor === 'yellow' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {resultadoAvaliacao.score_final}
                  </p>
                  <p className="text-sm font-medium mt-1 text-slate-600 dark:text-slate-400">
                    {resultadoAvaliacao.classificacao}
                  </p>
                </div>
              </div>
              <div className="lg:col-span-3">
                <div className="space-y-3">
                  {resultadoAvaliacao.resultados_dimensoes?.map(dim => (
                    <div key={dim.dimensao_id} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="dark:text-white">{dim.dimensao_nome}</span>
                        <span className="font-medium dark:text-white">{dim.score}/100</span>
                      </div>
                      <Progress value={dim.score} className="h-2" />
                    </div>
                  ))}
                </div>
                {resultadoAvaliacao.observacoes && (
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm font-medium dark:text-white">Observações:</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{resultadoAvaliacao.observacoes}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Atividade Recente */}
      {historico?.length > 0 && (
        <Card data-testid="card-atividade-recente">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-5 h-5 text-slate-500" />
              Atividade Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {historico.slice(0, 5).map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 py-2 border-b dark:border-slate-700 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-[#0066CC] mt-2 shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{entry.acao || entry.descricao || 'Alteração registada'}</p>
                    {entry.detalhes && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{entry.detalhes}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' }) : ''}
                    </p>
                    {entry.utilizador_nome && <p className="text-[10px] text-slate-400 dark:text-slate-500">{entry.utilizador_nome}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Moradas e Respostas Sociais por Morada */}
      <MoradasSection instituicaoId={id} valencias={dadosRelacionados?.valencias || []} />

      {/* Valencias - totais de todas as moradas + valências diretas */}
      {instituicao.valencias?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Respostas Sociais ({instituicao.valencias.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="global">
              <TabsList className="mb-4">
                <TabsTrigger value="global">
                  <Users className="w-4 h-4 mr-1.5" />Global
                </TabsTrigger>
                <TabsTrigger value="por-morada" disabled={!instituicao.moradas?.some(m => m.valencias?.length > 0)}>
                  <MapPin className="w-4 h-4 mr-1.5" />
                  Por Morada
                  {instituicao.moradas?.some(m => m.valencias?.length > 0) && (
                    <Badge className="ml-1.5 text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700">
                      {instituicao.moradas.filter(m => m.valencias?.length > 0).length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* TAB GLOBAL — totais por valência */}
              <TabsContent value="global">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {instituicao.valencias.map((v) => {
                    const masculino = v.utentes_masculino || 0;
                    const feminino = v.utentes_feminino || 0;
                    const total = v.numero_utentes || (masculino + feminino);
                    const valenciaKey = v.id || v.valencia_id || v.valencia_codigo;
                    return (
                      <div key={valenciaKey} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold dark:text-white">{v.valencia_nome || `Resposta Social (${v.valencia_id?.slice(0, 8)})`}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {[v.valencia_codigo, v.categoria].filter(Boolean).join(' • ')}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {total}/{v.capacidade_maxima || '-'} cap.
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded">
                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{masculino}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">Masculino</p>
                          </div>
                          <div className="p-2 bg-pink-50 dark:bg-pink-900/30 rounded">
                            <p className="text-lg font-bold text-pink-600 dark:text-pink-400">{feminino}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">Feminino</p>
                          </div>
                          <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded">
                            <p className="text-lg font-bold text-slate-700 dark:text-slate-200">{total}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* TAB POR MORADA — demografias por morada adicional */}
              <TabsContent value="por-morada">
                {!instituicao.moradas?.some(m => m.valencias?.length > 0) ? (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Nenhuma morada adicional tem respostas sociais com dados demográficos.</p>
                    <p className="text-xs mt-1">Edite a instituição, adicione uma morada e configure os utentes por resposta social.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(instituicao.moradas || []).filter(m => m.valencias?.length > 0).map((morada, mi) => {
                      const totalMorada = morada.valencias.reduce((s, v) => s + (v.numero_utentes || (v.utentes_masculino || 0) + (v.utentes_feminino || 0)), 0);
                      return (
                        <div key={morada.id || mi} className="border rounded-lg p-4 dark:border-slate-700">
                          {/* Cabeçalho da morada */}
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b dark:border-slate-700">
                            <MapPin className="w-4 h-4 text-[#0066CC] shrink-0" />
                            <div className="flex-1">
                              <span className="font-semibold text-sm">{morada.designacao || morada.morada || `Morada ${mi + 1}`}</span>
                              {morada.morada && morada.designacao && (
                                <span className="text-xs text-slate-400 ml-2">{morada.morada}</span>
                              )}
                              {morada.localidade && (
                                <span className="text-xs text-slate-400 ml-1">— {morada.localidade}</span>
                              )}
                            </div>
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                              {totalMorada} utentes
                            </Badge>
                          </div>

                          {/* Respostas sociais desta morada */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {morada.valencias.map((v, vi) => {
                              const masculino = v.utentes_masculino || 0;
                              const feminino = v.utentes_feminino || 0;
                              const total = v.numero_utentes || (masculino + feminino);
                              // Recolher faixas etárias deste utente (campos *_masculino/*_feminino)
                              const faixasKeys = Object.keys(v).filter(k => k.endsWith('_masculino') && k !== 'utentes_masculino');
                              return (
                                <div key={v.valencia_id || vi} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium">{v.valencia_nome}</p>
                                    <span className="text-xs font-semibold text-slate-600">{total} total</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-center mb-2">
                                    <div className="py-1 bg-blue-50 dark:bg-blue-900/20 rounded">
                                      <p className="text-base font-bold text-blue-600">{masculino}</p>
                                      <p className="text-[9px] text-slate-400">Masculino</p>
                                    </div>
                                    <div className="py-1 bg-pink-50 dark:bg-pink-900/20 rounded">
                                      <p className="text-base font-bold text-pink-500">{feminino}</p>
                                      <p className="text-[9px] text-slate-400">Feminino</p>
                                    </div>
                                  </div>
                                  {/* Faixas etárias detalhadas se disponíveis */}
                                  {faixasKeys.length > 0 && (
                                    <div className="mt-2 pt-2 border-t dark:border-slate-700 space-y-1">
                                      {faixasKeys.map(fkM => {
                                        const fkF = fkM.replace('_masculino', '_feminino');
                                        const faixaNome = fkM.replace('_masculino', '').replace(/_/g, ' ');
                                        const m = v[fkM] || 0;
                                        const f = v[fkF] || 0;
                                        if (m === 0 && f === 0) return null;
                                        return (
                                          <div key={fkM} className="grid grid-cols-3 gap-1 text-xs">
                                            <span className="text-slate-500 capitalize truncate">{faixaNome}</span>
                                            <span className="text-center text-blue-500 font-medium">{m}M</span>
                                            <span className="text-center text-pink-500 font-medium">{f}F</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Related Data */}
      <Tabs defaultValue="pedidos" className="w-full" onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-9">
          <TabsTrigger value="pedidos" className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            <span className="hidden lg:inline">Pedidos</span>
          </TabsTrigger>
          <TabsTrigger value="missoes" className="flex items-center gap-1">
            <HandHeart className="w-4 h-4" />
            <span className="hidden lg:inline">Missões</span>
          </TabsTrigger>
          <TabsTrigger value="tickets" className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden lg:inline">Tickets</span>
          </TabsTrigger>
          <TabsTrigger value="documentos-pendentes" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            <span className="hidden lg:inline">Docs</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="flex items-center gap-1">
            <History className="w-4 h-4" />
            <span className="hidden lg:inline">Histórico</span>
          </TabsTrigger>
          <TabsTrigger value="contactos-internos" className="flex items-center gap-1">
            <Phone className="w-4 h-4" />
            <span className="hidden lg:inline">Contactos</span>
          </TabsTrigger>
          <TabsTrigger value="mensagens" className="flex items-center gap-1">
            <Mail className="w-4 h-4" />
            <span className="hidden lg:inline">Mensagens</span>
          </TabsTrigger>
          <TabsTrigger value="avaliacoes" className="flex items-center gap-1">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden lg:inline">Avaliações</span>
          </TabsTrigger>
          <TabsTrigger value="utilizadores" className="flex items-center gap-1">
            <User className="w-4 h-4" />
            <span className="hidden lg:inline">Utilizadores</span>
          </TabsTrigger>
        </TabsList>

        {/* Pedidos Tab */}
        <TabsContent value="pedidos">
          <Card>
            <CardContent className="pt-6">
              {dadosRelacionados?.pedidos?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem pedidos registados</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Urgência</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dadosRelacionados?.pedidos?.map((pedido) => (
                      <TableRow key={pedido.id}>
                        <TableCell className="font-mono text-sm">{pedido.codigo}</TableCell>
                        <TableCell>{pedido.tipo}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            pedido.urgencia === 'Urgente' ? 'border-red-500 text-red-500' :
                            pedido.urgencia === 'Alta' ? 'border-orange-500 text-orange-500' : ''
                          }>
                            {pedido.urgencia}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(pedido.status)}>{pedido.status}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">{new Date(pedido.created_at).toLocaleDateString('pt-PT')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Missões Tab */}
        <TabsContent value="missoes">
          <Card>
            <CardContent className="pt-6">
              {dadosRelacionados?.missoes?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <HandHeart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem missões de voluntariado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Voluntários</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dadosRelacionados?.missoes?.map((missao) => (
                      <TableRow key={missao.id}>
                        <TableCell className="font-mono text-sm">{missao.codigo}</TableCell>
                        <TableCell>{missao.titulo}</TableCell>
                        <TableCell>{missao.tipo}</TableCell>
                        <TableCell>{missao.voluntarios_confirmados || 0} / {missao.num_voluntarios}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(missao.status)}>{missao.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card>
            <CardContent className="pt-6">
              {dadosRelacionados?.tickets?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem tickets de suporte</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Assunto</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dadosRelacionados?.tickets?.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell className="font-mono text-sm">{ticket.codigo}</TableCell>
                        <TableCell>{ticket.assunto}</TableCell>
                        <TableCell>{ticket.categoria}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(ticket.status)}>{ticket.status}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">{new Date(ticket.created_at).toLocaleDateString('pt-PT')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documentos Pendentes Tab (Conta Corrente) */}
        <TabsContent value="documentos-pendentes">
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Adicionar novo documento */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documentos Pendentes (Conta Corrente)
                </h4>
                
                <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                  <p className="text-xs text-slate-500 mb-2">Adicionar Documento Pendente:</p>
                  <div className="grid grid-cols-4 gap-2">
                    <Select
                      value={novoDocumento.tipo}
                      onValueChange={(v) => setNovoDocumento(prev => ({ ...prev, tipo: v }))}
                    >
                      <SelectTrigger className="h-8" data-testid="select-doc-tipo">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Estatutos">Estatutos</SelectItem>
                        <SelectItem value="Relatório Contas">Relatório Contas</SelectItem>
                        <SelectItem value="Plano Atividades">Plano Atividades</SelectItem>
                        <SelectItem value="Certidão Permanente">Certidão Permanente</SelectItem>
                        <SelectItem value="Declaração IRS">Declaração IRS</SelectItem>
                        <SelectItem value="Comprovativo NIF">Comprovativo NIF</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="h-8 col-span-2"
                      placeholder="Descrição..."
                      value={novoDocumento.descricao}
                      onChange={(e) => setNovoDocumento(prev => ({ ...prev, descricao: e.target.value }))}
                      data-testid="input-doc-descricao"
                    />
                    <Button 
                      size="sm" 
                      onClick={addDocumentoPendente}
                      className="h-8 bg-amber-600 hover:bg-amber-700"
                      data-testid="btn-add-documento"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {loadingTabs.documentos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    <span className="ml-2 text-slate-500">A carregar...</span>
                  </div>
                ) : documentosPendentes.length > 0 ? (
                  <div className="space-y-2">
                    {documentosPendentes.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">{doc.tipo}</p>
                          <p className="text-xs text-slate-500">{doc.descricao}</p>
                          {doc.data_limite && (
                            <p className="text-xs text-red-500 mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              Prazo: {new Date(doc.data_limite).toLocaleDateString('pt-PT')}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => marcarDocumentoEntregue(doc.id)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          data-testid={`btn-doc-entregue-${doc.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Entregue
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Sem documentos pendentes.
                  </p>
                )}
              </div>

              {/* Histórico de Documentos Entregues */}
              {documentosHistorico.length > 0 && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Documentos Entregues
                  </h4>
                  <div className="space-y-2">
                    {documentosHistorico.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border text-sm">
                        <div>
                          <span className="font-medium">{doc.tipo}</span>
                          <span className="text-slate-400 mx-2">-</span>
                          <span className="text-slate-500">{doc.descricao}</span>
                        </div>
                        <span className="text-xs text-green-600">
                          {doc.data_entrega && new Date(doc.data_entrega).toLocaleDateString('pt-PT')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documentos Carregados (existente) */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Documentos Carregados</h4>
                {dadosRelacionados?.documentos?.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Sem documentos carregados</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Validação</TableHead>
                        <TableHead>Validade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dadosRelacionados?.documentos?.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell>{doc.nome_original}</TableCell>
                          <TableCell>{doc.tipo_documento}</TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(doc.status_validacao === 'Aprovado' ? 'Aprovado' : doc.status_validacao === 'Rejeitado' ? 'Rejeitado' : 'Pendente')}>
                              {doc.status_validacao}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {doc.data_validade ? new Date(doc.data_validade).toLocaleDateString('pt-PT') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Histórico de Alterações Tab */}
        <TabsContent value="historico">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Histórico de Alterações de Dados
                </h4>
                <Button variant="outline" size="sm" onClick={loadHistorico}>
                  <Loader2 className={`w-4 h-4 mr-1 ${loadingTabs.historico ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
              
              {loadingTabs.historico ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                  <span className="ml-2 text-slate-500">A carregar histórico...</span>
                </div>
              ) : historico.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {historico.map((entry, idx) => (
                    <div key={entry.id || idx} className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-l-4 border-purple-400">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-purple-600">{entry.tipo_alteracao || 'Alteração'}</span>
                        <span className="text-xs text-slate-400">
                          {entry.data_alteracao && new Date(entry.data_alteracao).toLocaleDateString('pt-PT', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{entry.descricao}</p>
                      {entry.campo_alterado && (
                        <div className="mt-2 text-xs flex items-center gap-2">
                          <span className="text-slate-500">Campo: </span>
                          <span className="font-medium">{entry.campo_alterado}</span>
                          {entry.valor_anterior && (
                            <span className="text-red-500 line-through">{entry.valor_anterior}</span>
                          )}
                          {entry.valor_novo && (
                            <>
                              <span className="text-slate-400">→</span>
                              <span className="text-green-600">{entry.valor_novo}</span>
                            </>
                          )}
                        </div>
                      )}
                      {entry.utilizador && (
                        <p className="text-xs text-slate-400 mt-2">Por: {entry.utilizador}</p>
                      )}
                      {/* Botão para ver detalhes */}
                      {entry.audit_detalhes && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-xs text-purple-600"
                          onClick={() => setHistoricoDetalhe(entry)}
                        >
                          <Eye className="w-3 h-3 mr-1" /> Ver detalhes
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem histórico de alterações registado.</p>
                  <Button variant="link" className="mt-2" onClick={loadHistorico}>Carregar histórico</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contactos Internos Tab (Registo de Contactos) */}
        <TabsContent value="contactos-internos">
          <Card>
            <CardContent className="pt-6">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Registo de Contactos com a Instituição
                </h4>
                
                {/* Adicionar novo contacto */}
                <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                  <p className="text-xs text-slate-500 mb-2">Registar Novo Contacto:</p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <Select
                      value={novoContacto.tipo}
                      onValueChange={(v) => setNovoContacto(prev => ({ ...prev, tipo: v }))}
                    >
                      <SelectTrigger className="h-8" data-testid="select-contacto-tipo">
                        <SelectValue placeholder="Tipo de Contacto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Telefone">Telefone</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Reunião Presencial">Reunião Presencial</SelectItem>
                        <SelectItem value="Reunião Online">Reunião Online</SelectItem>
                        <SelectItem value="Visita">Visita à Instituição</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="h-8"
                      placeholder="Assunto"
                      value={novoContacto.assunto}
                      onChange={(e) => setNovoContacto(prev => ({ ...prev, assunto: e.target.value }))}
                      data-testid="input-contacto-assunto"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Textarea
                      className="flex-1 min-h-[60px]"
                      placeholder="Descrição / Notas do contacto..."
                      value={novoContacto.descricao}
                      onChange={(e) => setNovoContacto(prev => ({ ...prev, descricao: e.target.value }))}
                      data-testid="textarea-contacto-descricao"
                    />
                    <Button 
                      onClick={addContactoRegistado}
                      className="bg-blue-600 hover:bg-blue-700 self-end"
                      data-testid="btn-add-contacto"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Registar
                    </Button>
                  </div>
                </div>

                {/* Lista de contactos registados */}
                {loadingTabs.contactos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    <span className="ml-2 text-slate-500">A carregar contactos...</span>
                  </div>
                ) : contactosRegistados.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {contactosRegistados.map((contacto) => (
                      <div key={contacto.id} className="p-3 bg-white dark:bg-slate-800 rounded-lg border-l-4 border-blue-400">
                        <div className="flex justify-between items-start">
                          <div>
                            <Badge variant="outline" className="text-xs mb-1">{contacto.tipo}</Badge>
                            <p className="font-medium text-sm">{contacto.assunto}</p>
                            {contacto.descricao && (
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{contacto.descricao}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {contacto.data && new Date(contacto.data).toLocaleDateString('pt-PT')}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteContactoRegistado(contacto.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`btn-delete-contacto-${contacto.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {contacto.registado_por && (
                          <p className="text-xs text-slate-400 mt-2">Registado por: {contacto.registado_por}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Sem contactos registados com esta instituição.</p>
                    <Button variant="link" className="mt-2" onClick={loadContactos}>Carregar contactos</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mensagens Tab */}
        <TabsContent value="mensagens">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Mensagens da Central relacionadas com esta Instituição
                </h4>
                <Button variant="outline" size="sm" onClick={loadMensagens}>
                  <Loader2 className={`w-4 h-4 mr-1 ${loadingTabs.mensagens ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
              
              {loadingTabs.mensagens ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  <span className="ml-2 text-slate-500">A carregar mensagens...</span>
                </div>
              ) : mensagens.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {mensagens.map((msg) => (
                    <div key={msg.id} className={`p-4 rounded-lg border ${msg.lida ? 'bg-slate-50 dark:bg-slate-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={msg.tipo === 'entrada' ? 'default' : 'secondary'}>
                            {msg.tipo === 'entrada' ? 'Recebida' : 'Enviada'}
                          </Badge>
                          {!msg.lida && <Badge className="bg-blue-500">Nova</Badge>}
                        </div>
                        <span className="text-xs text-slate-400">
                          {msg.data && new Date(msg.data).toLocaleDateString('pt-PT', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <p className="font-medium text-sm">{msg.assunto}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{msg.conteudo}</p>
                      {msg.remetente && (
                        <p className="text-xs text-slate-400 mt-2">De: {msg.remetente}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem mensagens relacionadas com esta instituição.</p>
                  <Button variant="link" className="mt-2" onClick={loadMensagens}>Verificar mensagens</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Avaliações Tab */}
        <TabsContent value="avaliacoes">
          <Card>
            <CardContent className="pt-6">
              {dadosRelacionados?.avaliacoes?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sem avaliações registadas</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dadosRelacionados?.avaliacoes?.map((aval) => (
                    <div key={aval.id} className="p-4 border rounded-lg dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge variant={aval.tipo === 'Visita Presencial' ? 'default' : 'secondary'}>
                            {aval.tipo || aval.tipo_avaliacao || 'Avaliação'}
                          </Badge>
                          <Badge variant={aval.status === 'Aprovado' ? 'outline' : 'secondary'} className={aval.status === 'Aprovado' ? 'text-green-600 border-green-600' : ''}>
                            {aval.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`text-2xl font-bold ${
                            aval.score_final >= 80 ? 'text-green-600' :
                            aval.score_final >= 60 ? 'text-[#0066CC]' :
                            aval.score_final >= 40 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {aval.score_final?.toFixed(1) || '0'}
                          </div>
                          <Badge variant="outline">{aval.classificacao || '-'}</Badge>
                        </div>
                      </div>
                      
                      {/* Scores por dimensão */}
                      {aval.scores_por_dimensao && Object.keys(aval.scores_por_dimensao).length > 0 && (
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                          {Object.entries(aval.scores_por_dimensao).map(([dim, score]) => (
                            <div key={dim} className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                              <p className="text-xs text-slate-500">{dim.length < 5 ? dim : dim.slice(0, 3)}</p>
                              <p className="font-bold text-sm">{typeof score === 'number' ? score.toFixed(0) : score}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {aval.data_realizacao || aval.created_at?.split('T')[0] || '-'}
                        </div>
                        {aval.visita_id && (
                          <span className="text-xs">Via visita presencial</span>
                        )}
                      </div>

                      {(aval.observacoes_visitador || aval.observacoes) && (
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 border-t pt-2">
                          {aval.observacoes_visitador || aval.observacoes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Utilizadores Tab */}
        <TabsContent value="utilizadores">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Utilizadores do Portal</h3>
                <Badge variant="outline">{instituicao?.utilizadores_portal?.length || 0} utilizador(es)</Badge>
              </div>
              {!instituicao?.utilizadores_portal || instituicao.utilizadores_portal.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nenhum utilizador do portal associado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {instituicao.utilizadores_portal.map((user, idx) => (
                    <div 
                      key={user.id || idx}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/utilizadores-portal?user=${user.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0066CC]/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#0066CC]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.nome || user.email}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={user.role === 'INST_ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-800'}>
                          {user.role === 'INST_ADMIN' ? 'Administrador' : 'Utilizador'}
                        </Badge>
                        <Badge className={user.ativo !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {user.ativo !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
